import React from "react";
import {
  ArrowRightIcon,
  BookOpenCheckIcon,
  CalendarIcon,
  ChartIcon,
  CircleCheckBigIcon,
  DownloadIcon,
  ClipboardCheckIcon,
  FileChartIcon,
  NetworkIcon,
  ShieldIcon,
  TargetIcon,
  UsersRoundIcon,
} from "./landing/Icons";
import GuideDownload from "./landing/guide-download/GuideDownload";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import SupportTierCards from "./landing/programmes/SupportTierCards";
import {
  Breadcrumb,
  CONTAINER,
  GRID_BACKDROP,
  PrimaryLink,
  Section,
  SectionHeading,
  SectionLabel,
  SecondaryLink,
} from "./landing/ui";

const STALL_REASONS = [
  {
    Icon: ClipboardCheckIcon,
    title: "Successors are named but never developed",
    body: "The succession chart is complete, but the people on it have no structured way to build the judgement, confidence and exposure the role needs.",
  },
  {
    Icon: BookOpenCheckIcon,
    title: "Critical know-how sits with a few senior people or expatriates",
    body: "Context, relationships and decision-making experience are hard to pass on when the knowledge lives in people rather than in a repeatable process.",
  },
  {
    Icon: ChartIcon,
    title: "Leadership can't see who is actually ready",
    body: "HR can report names and activity, but leadership still lacks consistent evidence of progress, new behaviours at work and readiness for a bigger role.",
  },
] as const;

const BUILDING_BLOCKS = [
  {
    Icon: TargetIcon,
    title: "Critical roles and successors agreed",
    body: "We help your HR team pin down the roles that matter, the successors to develop and the leadership expectations the journey works towards.",
  },
  {
    Icon: NetworkIcon,
    title: "Relevant mentor matching",
    body: "Each successor is matched with a FutureLab industry mentor, one of your senior leaders or expatriates, or someone from a blended pool, based on their goals.",
  },
  {
    Icon: CalendarIcon,
    title: "A journey that fits your timeline",
    body: "We shape the length, session rhythm and mix of one-to-one mentoring and group learning around your priorities.",
  },
  {
    Icon: UsersRoundIcon,
    title: "Emerging and senior tracks",
    body: "Separate journeys for emerging and senior talent, so goals, conversations and practice match each group's level of responsibility.",
  },
  {
    Icon: ShieldIcon,
    title: "Programme management on one platform",
    body: "Choose the support you need across onboarding, admin, matching, scheduling, engagement tracking and participant communications.",
  },
  {
    Icon: FileChartIcon,
    title: "A final impact report for leadership",
    body: "Mentee and mentor evaluations, participation data and workplace examples, brought together in one report for leadership review.",
  },
] as const;

const JOURNEY = [
  {
    when: "Month 1",
    title: "Onboard and match",
    body: "Onboard mentors and successors, confirm development priorities, then match pairs around goals and support needs.",
  },
  {
    when: "Months 1–3",
    title: "Set leadership direction",
    body: "Complete the first two sessions, reflect on how each successor leads and set clear development goals.",
  },
  {
    when: "Month 3",
    title: "Regroup at the halfway point",
    body: "Bring participants together in a group learning circle to compare experience and refocus the second half.",
  },
  {
    when: "Months 4–6",
    title: "Apply, evaluate and report",
    body: "Complete the last two sessions, put new behaviours into practice at work, then close with graduation and the final impact report.",
  },
] as const;

const MEASURES = [
  "Session completion",
  "Retention in the programme",
  "Self-reported growth in targeted leadership capabilities",
  "Growth in targeted leadership capabilities observed by mentors",
  "Real workplace examples of new leadership behaviours",
  "Readiness for broader leadership responsibility",
] as const;

// From the reference site's client example; confirm the figures before this goes live.
const CLIENT_EXAMPLE = {
  setup: "This client chose six months, two tracks and FutureLab industry mentors.",
  context: "The programme supported 27 women leaders at a global offshore energy company.",
  results: [
    { value: "89%", label: "of participants stayed active through the programme" },
    { value: "92%", label: "completed all four mentoring sessions" },
    { value: "100%", label: "of survey respondents put at least one new leadership behaviour into practice at work" },
    { value: "85%", label: "said mentoring contributed significantly to the change" },
  ],
  seniorTrack:
    "In the senior track, 55% reported more confidence taking on broader leadership responsibility, and 55% stepped into opportunities with wider leadership impact.",
  quote:
    "I was content with my current career level until a mentorship program made me realise I should pursue the career dream I had many years ago.",
  source: "Based on the post-programme evaluation, 2026 (20 of 24 active participants responded).",
} as const;

type Props = {
  programmePlanUrl: string;
  midaGuidePageUrl: string;
  programmesUrl: string;
  businessCaseUrl: string;
};

export default function SuccessionPlanningPage({ programmePlanUrl, midaGuidePageUrl, programmesUrl, businessCaseUrl }: Props) {
  return (
    <LeadFormProvider>
      <section className="tw-relative tw-bg-[#f4f8fc] tw-pb-24 tw-pt-20 sm:tw-pb-16 sm:tw-pt-10">
        <div aria-hidden className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

        <div
          className={`${CONTAINER} tw-relative tw-grid tw-grid-cols-[1.08fr_.72fr] tw-items-end tw-gap-10 sm:tw-grid-cols-1`}
        >
          <div className="tw-min-w-0">
            <Breadcrumb current="Succession planning" parents={[{ label: "Programmes", href: programmesUrl }]} />
            {/* The approved H1, mirrored in static_pages/succession_planning.html.erb; keep both in step. */}
            <h1 className="tw-mt-6 tw-mb-0 tw-max-w-4xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em] tw-text-[#10233f]">
              A succession planning programme that gets successors ready, not just named
            </h1>
            <p className="tw-mt-6 tw-mb-0 tw-max-w-3xl tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-text-lg">
              Our succession planning programme helps HR and talent teams in Malaysia and Southeast Asia
              prepare successors for critical roles through structured mentoring. You choose the mentors,
              timeline and format. The platform and our programme team help pass on knowledge, build
              leadership readiness and show leadership clear evidence of progress.
            </p>

            <div className="tw-mt-8 tw-flex tw-flex-row tw-gap-3 sm:tw-flex-col">
              <PrimaryLink href={programmePlanUrl} data-lead-form="programme_plan">
                Get a succession programme plan
                <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </PrimaryLink>
              <SecondaryLink href={businessCaseUrl}>Build your business case</SecondaryLink>
            </div>
          </div>

          <aside
            aria-label="Typical succession programme"
            className="tw-min-w-0 tw-rounded-[28px] tw-border tw-border-[#c5dcea] tw-bg-white tw-p-8 tw-shadow-[0_24px_60px_rgba(23,72,111,.12)] xs:tw-p-6"
          >
            <SectionLabel>Typical example</SectionLabel>
            <p className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-tracking-[-.045em] tw-text-[#173754]">6 months</p>
            <p className="tw-mt-6 tw-mb-0 tw-border-0 tw-border-t tw-border-solid tw-border-[#d8e6f0] tw-pt-6 tw-font-bold tw-leading-7 tw-text-[#304d68]">
              Four one-to-one mentoring sessions, with a group check-in at the halfway point.
            </p>
            <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#60768c]">
              Length, format and mentors can all be adjusted.
            </p>
          </aside>
        </div>
      </section>

      <Section>
        <div className="tw-max-w-3xl">
          <SectionLabel>The gap between a plan and a pipeline</SectionLabel>
          <SectionHeading>Why most succession plans stall.</SectionHeading>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-1">
          {STALL_REASONS.map(({ Icon, title, body }) => (
            <article key={title} className="tw-rounded-[26px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 xs:tw-p-6">
              <span
                aria-hidden
                className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#eaf6fd] tw-text-[#087bd3]"
              >
                <Icon className="tw-h-6 tw-w-6" />
              </span>
              <h3 className="tw-mt-6 tw-mb-0 tw-text-2xl tw-font-black tw-leading-tight tw-tracking-[-.03em] tw-text-[#173754]">
                {title}
              </h3>
              <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc]">
        <div className="tw-grid tw-grid-cols-[.72fr_1.28fr] tw-items-end tw-gap-8 sm:tw-grid-cols-1 sm:tw-gap-5">
          <div>
            <SectionLabel>Built around your succession priorities</SectionLabel>
            <SectionHeading>What we help you build</SectionHeading>
          </div>
          <p className="tw-m-0 tw-max-w-2xl tw-justify-self-end tw-text-lg tw-leading-8 tw-text-[#60768c] sm:tw-justify-self-start">
            We connect role priorities, mentoring conversations, practice at work and reporting in one
            journey designed around your organisation.
          </p>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-2 xs:tw-grid-cols-1">
          {BUILDING_BLOCKS.map(({ Icon, title, body }) => (
            <article key={title} className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6">
              <Icon aria-hidden className="tw-h-6 tw-w-6 tw-text-[#087bd3]" />
              <h3 className="tw-mt-5 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <div className="tw-max-w-3xl">
          <SectionLabel>How it can run</SectionLabel>
          <SectionHeading>A typical six-month journey</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">
            Treat this as a starting point. We can change the timing, session rhythm and group
            activities to fit your existing talent programme.
          </p>
        </div>

        <ol
          aria-label="Six-month succession planning programme timeline"
          className="tw-mt-12 tw-mb-0 tw-grid tw-list-none tw-grid-cols-4 tw-gap-5 tw-p-0 md:tw-grid-cols-2 sm:tw-mt-9 xs:tw-grid-cols-1"
        >
          {JOURNEY.map((stage, index) => (
            <li key={stage.title} className="tw-relative tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6">
              <span className="tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#0068b8]">
                {stage.when}
              </span>
              <h3 className="tw-mt-4 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{stage.title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{stage.body}</p>
              <span aria-hidden className="tw-absolute tw-right-5 tw-top-5 tw-text-sm tw-font-black tw-text-[#a9c4d9]">
                {String(index + 1).padStart(2, "0")}
              </span>
            </li>
          ))}
        </ol>
      </Section>

      <section className="tw-bg-[#10233f] tw-py-24 tw-text-white sm:tw-py-20">
        <div className={`${CONTAINER} tw-grid tw-grid-cols-[.72fr_1.28fr] tw-gap-10 sm:tw-grid-cols-1`}>
          <div>
            <SectionLabel tone="mint">What you can measure</SectionLabel>
            <SectionHeading>Track development without calling readiness too early.</SectionHeading>
            <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#b9cddd]">
              The programme records participation, development signals and practice at work for
              leadership to review.
            </p>
          </div>

          <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-2 tw-content-start tw-gap-3 tw-p-0 xs:tw-grid-cols-1">
            {MEASURES.map((measure) => (
              <li
                key={measure}
                className="tw-flex tw-gap-3 tw-rounded-2xl tw-border tw-border-white/15 tw-bg-white/[.06] tw-p-5 tw-font-bold tw-leading-6 tw-text-[#d9e7f1]"
              >
                <CircleCheckBigIcon aria-hidden className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-[#70d9ce]" />
                {measure}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Section className="tw-bg-[#f4f8fc]">
        <div className="tw-max-w-4xl">
          <SectionLabel>Client example</SectionLabel>
          <SectionHeading>How one client ran it</SectionHeading>
          <p className="tw-mt-6 tw-mb-0 tw-text-lg tw-font-black tw-leading-8 tw-text-[#304d68]">{CLIENT_EXAMPLE.setup}</p>
          <p className="tw-mt-3 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">{CLIENT_EXAMPLE.context}</p>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-4 tw-gap-4 md:tw-grid-cols-2 sm:tw-mt-9 xs:tw-grid-cols-1">
          {CLIENT_EXAMPLE.results.map((result) => (
            <article key={result.label} className="tw-rounded-[24px] tw-border tw-border-[#c8deec] tw-bg-white tw-p-6">
              <p className="tw-m-0 tw-text-4xl tw-font-black tw-tabular-nums tw-tracking-[-.045em] tw-text-[#0068b8]">
                {result.value}
              </p>
              <p className="tw-mt-3 tw-mb-0 tw-font-bold tw-leading-7 tw-text-[#304d68]">{result.label}</p>
            </article>
          ))}
        </div>

        <div className="tw-mt-5 tw-grid tw-grid-cols-[1.1fr_.9fr] tw-gap-5 sm:tw-grid-cols-1">
          <div className="tw-rounded-[26px] tw-bg-[#087bd3] tw-p-9 tw-text-white xs:tw-p-7">
            <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#d9efff]">Senior track</p>
            <p className="tw-mt-4 tw-mb-0 tw-text-2xl tw-font-black tw-leading-tight">{CLIENT_EXAMPLE.seniorTrack}</p>
          </div>
          <blockquote className="tw-m-0 tw-rounded-[26px] tw-border tw-border-[#c8deec] tw-bg-white tw-p-9 xs:tw-p-7">
            <p className="tw-m-0 tw-text-xl tw-font-black tw-leading-8 tw-tracking-[-.02em] tw-text-[#173754]">
              &ldquo;{CLIENT_EXAMPLE.quote}&rdquo;
            </p>
          </blockquote>
        </div>

        <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">{CLIENT_EXAMPLE.source}</p>
        <a
          href={programmePlanUrl}
          data-lead-form="programme_plan"
          className="tw-mt-7 tw-inline-flex tw-min-h-11 tw-items-center tw-gap-2 tw-font-black tw-text-[#0068b8] tw-no-underline hover:tw-text-[#005a9f]"
        >
          Talk to us about building your pipeline
          <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
        </a>
      </Section>

      <section className="tw-py-24 sm:tw-py-14">
        <div className={CONTAINER}>
          <div className="tw-relative tw-grid tw-grid-cols-[1.2fr_.8fr] tw-items-center tw-overflow-hidden tw-rounded-[30px] tw-bg-[#087bd3] tw-p-12 tw-text-white sm:tw-grid-cols-1 sm:tw-p-10 xs:tw-rounded-[24px] xs:tw-p-6">
            <div
              aria-hidden
              className="tw-pointer-events-none tw-absolute -tw-right-20 -tw-top-20 tw-h-72 tw-w-72 tw-rounded-full tw-border-[42px] tw-border-solid tw-border-white/10"
            />
            <div className="tw-relative tw-min-w-0">
              <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#d9efff]">
                Malaysia · 1 January 2027
              </p>
              <h2 className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-leading-tight tw-tracking-[-.04em] xs:tw-text-3xl">
                Employing expatriates in Malaysia?
              </h2>
              <p className="tw-mt-5 tw-mb-0 tw-leading-7 tw-text-white">
                Under Malaysia&rsquo;s Revised Expatriate Salary Policy, employers must submit and comply
                with a succession plan from 1 January 2027. The official FAQ describes a structured plan to
                prepare local employees to take over expatriate roles, covering the roles identified,
                training, mentoring and knowledge transfer, a capability-building timeline and continuity
                planning.
              </p>
              <p className="tw-mt-4 tw-mb-0 tw-font-bold tw-leading-7 tw-text-white">
                Our programmes give you the mentoring and knowledge-transfer structure the plan asks for,
                with records to show it&rsquo;s happening.
              </p>
              <p className="tw-mt-4 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#d9efff]">
                Check your obligations with MIDA or your immigration adviser. This is not legal advice.
              </p>
            </div>
            <div className="tw-relative tw-justify-self-end tw-text-center sm:tw-mt-8 sm:tw-justify-self-start sm:tw-text-left xs:tw-w-full">
              <PrimaryLink
                href={midaGuidePageUrl}
                data-guide-download
                aria-haspopup="dialog"
                variant="inverse"
                className="xs:tw-w-full"
              >
                Get the MIDA succession plan guide
                <DownloadIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </PrimaryLink>
              <p className="tw-mt-3 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#d9efff]">Free PDF · 7 pages</p>
              <a
                href={midaGuidePageUrl}
                className="tw-mt-2 tw-inline-flex tw-min-h-11 tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-white tw-underline tw-decoration-white/40 tw-underline-offset-4 hover:tw-text-white hover:tw-decoration-white"
              >
                Or read the guide online
                <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc]">
        <div className="tw-max-w-3xl">
          <SectionLabel>Flexible by design</SectionLabel>
          <SectionHeading>Run it your way</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">
            You decide how the programme fits your people, systems and internal capacity. We provide the
            platform and the level of support you need.
          </p>
        </div>

        <div className="tw-mt-10">
          <SupportTierCards />
        </div>
      </Section>

      <GuideDownload />
    </LeadFormProvider>
  );
}
